import React, { useEffect, useMemo, forwardRef, useCallback } from 'react';
import { Alert, Button, Form, Tooltip } from 'antd'
import { HelpIcon } from 'components/Icons';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import { FieldTypes , generateFormFields } from 'utils/formUtils';
import GenericMappingForm from '../GenericMappingForm';
import _ from 'lodash';
import { useRef } from 'react';
import { useState } from 'react';
import Vocabulary from 'components/Vocabulary';
import { useFeatureFlag } from 'contexts/FeatureFlagContext';

const initialFormValues = {
  product: undefined,
  subProduct: undefined,
  environment: undefined,
  projectList: [{ project: undefined }]
};

export const DELIMITER = ":";
export const VERSION_DELIMITER = ";"

const STATUS_OPTIONS = ["NEW", "PATCHED", "REMEDIATION_REQUIRED", "REMEDIATION_COMPLETE","MITIGATED", "IGNORED", "NEEDS_REVIEW"];

const BlackDuckMappingForm = forwardRef(({disableProjectFields = false, ...props}, ref) => {
  const projectsRef = useRef({});
  const versionsRef = useRef({});
  const versionsApiRef = useRef([]);
  const [form] = Form.useForm();
  const cancelRef = useRef([]);
  const featureFlag = useFeatureFlag();
  const toolConfigId = props.record ? props.record.id : undefined;
  const [initialValues] = useState(() => {
    if (props.record) {
      return [];
    }
    return [{ ...initialFormValues, ..._.cloneDeep(DEFAULT_STATUS_MAPPING) }];
  });
  const [values, setValues] = useState(initialValues);
  const [versionChanged, setVersionChanged]  = useState(false);
 
  const STATUS =  useMemo(() => ["open", "confirmed",  "acceptrisk", "falsepositive", "mitigated", "suppressed", "triage", "inProgress", "controlled"], []);

  const [mappedProjectVerionList] = useState(() => {
    // this state contains the already mapped projects/versions
    // This is stored because the project and version dropdowns are paginated
    // and if the option selected is not on 2st page, then the ID is displayed as this is how select works
    if(props.record){
      const { project: projectVerionNameString, projectId: projectVersionIdString, project_version } = props.record;
      const projectVersionNameArray = projectVerionNameString.split(",");
      const projectVersionIdArray = projectVersionIdString.split(",");
      const projects = [], versions = {}
      _.forEach(projectVersionIdArray, (projectVersionId, i) => {
        // for Blackduck there is chance of projectName like test:abc 
        const [projectId, versionId] = projectVersionId.split(DELIMITER);
        const index = projectVersionNameArray[i].lastIndexOf(DELIMITER)
  
        const projectName = projectVersionNameArray[i].substring(0, index === -1 ? undefined : index);
        const versionName = index === -1 ? undefined : projectVersionNameArray[i].substring(index + 1)
        projects.push({ id: projectId, name: projectName });
        projectsRef.current = {...projectsRef.current, [projectId]: projectName }
        if(versionId){
          const versionNameArray = _.isEmpty(project_version) ? versionName.split(VERSION_DELIMITER): project_version.split(',');
          _.forEach(versionId.split(VERSION_DELIMITER), (verId, index) => {
            versions[projectId] = { id: verId, name: versionNameArray[index] }
            versionsRef.current = {...versionsRef.current, [verId]: versionNameArray[index] }
          })
        }
      });
      return {
        projects: _.uniqBy(projects, "id"),
        versions
      }
    }
    return { projects: [], versions: {} }
  })

  useEffect(() => {
    // cancelling all version APIs on component unmount
    const cancels = cancelRef.current;
    return () => _.forEach(cancels, cancel => cancel());
  }, []);

  const fetchVersion = useCallback((params) => {
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId, toolConfigId,  ...params });
    return {
      promise: promise.then(response => {
        // merging the already mapped versions to the fetched list so that if the selected value is not in the option list
        // then id should not be displayed
        if(mappedProjectVerionList.versions[params.project] && !_.isEmpty(response.projects)){
          response.projects = [...response.projects, mappedProjectVerionList.versions[params.project]];
        }
        _.forEach(response.projects, p => {
          versionsRef.current[p.id] = p.name;
        });
        // merging the already mapped versions to the fetched list so that if the selected value is not in the option list
        // then id should not be displayed
        if(mappedProjectVerionList.versions[params.project] && !_.isEmpty(response.projects)){
          response.projects = [...response.projects, mappedProjectVerionList.versions[params.project]];
        }
        return response;
      }),
      cancel
    }
  }, [props.loginConfigId, props.toolName, mappedProjectVerionList, toolConfigId]);

  const fetchProjects = useCallback((params) => {
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId,toolConfigId,  ...params });
    return {
      promise: promise.then(response => {
        // merging the already mapped versions to the fetched list so that if the selected value is not in the option list
        // then id should not be displayed
        if(mappedProjectVerionList.projects.length > 0 && !_.isEmpty(response.projects)){
          response.projects = [...response.projects, ...mappedProjectVerionList.projects];
        }
        _.forEach(response.projects, p => {
          projectsRef.current[p.id] = p.name;
        });
        // merging the already mapped versions to the fetched list so that if the selected value is not in the option list
        // then id should not be displayed
        if(mappedProjectVerionList.projects.length > 0 && !_.isEmpty(response.projects)){
          response.projects = [...response.projects, ...mappedProjectVerionList.projects];
        }
        return response;
      }),
      cancel
    }
  }, [props.loginConfigId,props.toolName, mappedProjectVerionList, toolConfigId]);

  const getExtraFieldsData = useCallback((config) => {
    const { projectList } = config;
    const checkEmpty = (status) => _.isEmpty(status) ? [] : status;

    const projectVersionNameList = [];
    const projectId = _.map(projectList, ({ project, version }) => {
      const projectName = projectsRef.current[project];
      const versionName = _.isArray(version)? _.map(version, v => versionsRef.current[v]).join(VERSION_DELIMITER) : versionsRef.current[version];
      if(!_.isEmpty(version)){
        projectVersionNameList.push(projectName + DELIMITER + versionName);
        return `${project}${DELIMITER}${_.isArray(version) ? version.join(VERSION_DELIMITER): version}`;
      }
      projectVersionNameList.push(projectName);
      return project;
    });

    const jsonData = {status: STATUS.reduce((acc, status) => {
      acc[status] = checkEmpty(config[status]);
      return acc
    }, {})};
    
    return ({
      projectId: projectId.join(),
      project: projectVersionNameList.join(),
      ...(featureFlag.BLACKDUCK_BIDIRECTIONAL_FLOW && {customFieldMapping: JSON.stringify(jsonData)}), // featureFlag for now
      //send the property received in config
      ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id })
    });
  }, [featureFlag.BLACKDUCK_BIDIRECTIONAL_FLOW, STATUS]);

  const parseExtraFieldsDataFromResponse = useCallback((config) => {
    const { projectId , customFieldMapping} = config;
    const customMappingJson = JSON.parse(customFieldMapping || "{}");
    const status = customMappingJson?.status;
    const projectWithVersions = projectId?.split(',');
    const projects = _.map(projectWithVersions, projectWithVersion => {
      const index = projectWithVersion.lastIndexOf(DELIMITER);
      if(index !== -1){
        const project = projectWithVersion.substring(0, index);
        const version = projectWithVersion.substring(index + 1, projectWithVersion.length);
        return { project, version: version.split(VERSION_DELIMITER) };
      }
      return { project: projectWithVersion }
    });
    return ({
      projectList: projects,
      ...status
    });
  }, []);
 
  const onValuesChange = useCallback((values, ...args) => {
    _.find(STATUS, status => {
      if (values.configurations[0]?.[status]) {
        const values = form.getFieldsValue().configurations;
        setValues(values);
        return true;
      }
    })
    if(values.configurations){
      let isProjectChanged = false;
        const formValues = form.getFieldsValue();
        _.forEach(values.configurations, (configs, index) => {

          // If project list is greater than 1, means that a new blank row is added
          // no need to clear the version fields
          if(!configs || !configs.projectList || configs.projectList.length > 1){
            return;
          }

          // since this is a list field, onchange will trigger only for the changed row.
          // if change is done at 2nd row, data received in this callback will only contain 2nd row data
          // configs.projectList is given as string when we search in the field
          if(!configs || _.isString(configs.projectList)){
            return;
          }
          _.forEach(configs.projectList, (object, i) => {

            // since this is a list field, onchange will trigger only for the changed row.
            // if change is done at 2nd row, data received in this callback will only contain 2nd row data
            // Also checking if the object contains `project` and nothing else 
            // because we need to reset the version field only if the project is changed
            // and this change is also triggered when rows are added or deleted, so we need to ignore it
            if(object && object.project && Object.keys(object).length === 1){
              console.log("Project changed", index, i);
              isProjectChanged = true;
              formValues.configurations[index].projectList[i].version = undefined;
            }
          })
        });
        if(isProjectChanged){
          form.setFieldsValue(formValues);
        }
    }
  }, [form, STATUS]);

  const projectListRender = useCallback((listField) => {

    // This is a hack, we are storing the api method for version in a ref
    // as it is dependant on `listField` param of this method
    // that is why we cannot move it to `useCallback`
    // For a `listField.name` we are defining the method only once and using the method if it alreadr exists in the ref
    // Reason for doing this is that the method reference will always change and will trigger API on every render
    // It also causes a visible bug on UI as when the API triggers, the selected value disappears as we don't show value when data is loading
    let versionAPI = versionsApiRef.current[listField.name];
    if(!versionAPI){
      versionAPI = versionsApiRef.current[listField.name] = ({ listFieldName: nestedListFieldName, ...params }) => {
        const allConfigs = form.getFieldsValue().configurations;
        const project = allConfigs[listField.name]?.projectList[nestedListFieldName]?.project;
        if (!project) {
          return { promise: Promise.resolve([]), cancel: _.noop };
        }
        return fetchVersion({ ...params, project });
      }
    }
    // 

    const __fields = [{
      name: 'project',
      type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
      disabled: disableProjectFields,
      colSpan: 9,
      // loading: _.isUndefined(projects),
      props: {
        showValueOnload: false,
        autoFocus: !disableProjectFields,
        optionsMutator: (projects) => {
          const selected = _.compact(_.map(form.getFieldsValue().configurations?.[listField.name]?.projectList, p => p?.project));
          return _.map(projects, p => ({ ...p, disabled: _.includes(selected, p.id) }));
        },
        api: fetchProjects
      },
      rules: [{ required: true, message: 'Blackduck Hub Project is required' }]
    }, {
      name: 'version',
      type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
      disabled: disableProjectFields,
      colSpan: 12,
      props: {
        placeholder: "Leave blank to fetch from latest version",
        api: versionAPI,
        mode: "multiple",
        setExtraProps: (field, nestedListField) => {
          const allConfigs = form.getFieldsValue().configurations;
          const project = allConfigs[listField.name]?.projectList[nestedListField.name]?.project;
          return {
            showValueOnload: false,
            resetOptionsProp: project
          }
        },
        onChange: () => {
          setVersionChanged(!_.isEmpty(props.record))
        }
      },
    }, {
      name: "add",
      colSpan: 2,
      render: ({ remove, name, fields }) => {
        return <Button disabled={fields.length === 1} type='dashed' onClick={() => { remove(name) }}>-</Button>;
      }
    }]

    return (
      <div className='m-b-sm'>
        <Form.List name={[listField.name, "projectList"]}>
          {(fields, { add, remove }) => {
            return (
              <div>
                {_.map(fields, field => {
                  return generateFormFields(__fields, 9, { ...field/* , name: [listField.name, field.name] */, add, remove, fields }, field.name);
                })}
                <Button size='small' type='dashed' onClick={() => add({ project: undefined })} disabled={disableProjectFields}>+ Add Project</Button>
                {versionChanged && <Alert className="m-t-m" message="Adding/removing version from an existing mapping might resolve findings from the previous mapped version." type="warning" />}
              </div>
            )
          }}
        </Form.List>
      </div>
    )
  }, [disableProjectFields, form, fetchProjects, fetchVersion, versionChanged, props.record]);

  const getFormOptions = useCallback((fieldKey) => {

    let selectedValues = values?.[0];
    // fetch selected data from the form while edit in customFieldMapping
    if (!selectedValues && props.record?.customFieldMapping) {
      selectedValues = parseExtraFieldsDataFromResponse(props.record);
    }

    let formData = _.omit(selectedValues, fieldKey);
    let selectedKeys = _.flatMap(formData).filter(key => key !== undefined);
    return _.map(STATUS_OPTIONS, option => ({
      label: _.capitalize(_.replace(option, '_', ' ')), 
      value: option, 
      disabled: selectedKeys.indexOf(option) !== -1
    }))
  }, [values, props.record,parseExtraFieldsDataFromResponse]);

  const _fields = useMemo(() => [{
    name: "label",
    type: FieldTypes.LABEL,
    value: <b className='required-mark'>Project</b>,
    style: {marginBottom: -24},
    colSpan: 9
  }, {
    name: "label",
    type: FieldTypes.LABEL,
    value: <b>Version <Tooltip title="Leave the version field blank to get findings from latest version only"><HelpIcon/></Tooltip></b>,
    style: {marginBottom: -24},
    colSpan: 9
  }, {
    name: (listField) => [listField.name, "projectList"],
    render: projectListRender
  },
  ...(
    featureFlag.BLACKDUCK_BIDIRECTIONAL_FLOW
    ?
    [
      {
        name: "open",
        label: <Vocabulary name = "open"/>,
        colSpan: 4,
        type: FieldTypes.MULTI_SELECT,
        options: getFormOptions("open"),
        props: {
          allowClear: true
        }
      },
      {
        name: "confirmed",
        colSpan: 4,
        label: <Vocabulary name = "confirmed"/>,
        type: FieldTypes.MULTI_SELECT,
        options: getFormOptions("confirmed"),
        props: {
          allowClear: true
        }
      },
      {
        name: "acceptrisk",
        colSpan: 4,
        label:  <Vocabulary name = "acceptRisk"/>,
        type: FieldTypes.MULTI_SELECT,
        options: getFormOptions("acceptrisk"),
        props: {
          allowClear: true
        }
      },
      {
        name: "falsepositive",
        colSpan: 4,
        label: <Vocabulary name = "falsePositive"/>,
        type: FieldTypes.MULTI_SELECT,
        options: getFormOptions("falsePositive"),
        props: {
          allowClear: true
        }
      },
      {
        name: "mitigated",
        colSpan: 4,
        label: <Vocabulary name = "mitigated"/>,
        type: FieldTypes.MULTI_SELECT,
        options: getFormOptions("mitigated"),
        props: {
          allowClear: true
        }
      },
      {
        name: "suppressed",
        colSpan: 4,
        label: <Vocabulary name = "suppressed"/>,
        type: FieldTypes.MULTI_SELECT,
        options: getFormOptions("suppressed"),
        props: {
          allowClear: true
        }
      },
      {
      name: "triage",
      colSpan: 4,
      label: <Vocabulary name = "triageStatus"/>,
      type: FieldTypes.MULTI_SELECT,
      options: getFormOptions("triage"),
      props: {
        allowClear: true
      }
      },
      {
        name: "inProgress",
        colSpan: 4,
        label: <Vocabulary name = "inProgressStatus"/>,
        type: FieldTypes.MULTI_SELECT,
        options: getFormOptions("inProgress"),
        props: {
          allowClear: true
        }
      },
      {
        name: "controlled",
        colSpan: 4,
        label: <Vocabulary name = "controlledStatus"/>,
        type: FieldTypes.MULTI_SELECT,
        options: getFormOptions("controlled"),
        props: {
          allowClear: true
        }
      }
    ]
    :
    []
  ),
   {
    type: FieldTypes.DIVIDER
  }], [projectListRender, getFormOptions, featureFlag.BLACKDUCK_BIDIRECTIONAL_FLOW]);

  return (
    <GenericMappingForm
      form={form}
      ref={ref}
      getExtraFieldsData={getExtraFieldsData}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      extraFields={_fields}
      formDefaultValues={initialValues}
      newRowDefaultValue={initialValues[0]}
      {...props}
      onValuesChange={onValuesChange}
    />
  )
})

export default BlackDuckMappingForm;

const DEFAULT_STATUS_MAPPING = {
  acceptrisk: ["IGNORED"],
  mitigated: ["REMEDIATION_COMPLETE","MITIGATED","PATCHED"],
  open: ["NEW", "REMEDIATION_REQUIRED", "NEEDS_REVIEW"],
  
};
